import { useMemo } from 'react';
import { INTL_LOCALES } from './types';
import { useT } from './useT';

export interface Formatters {
  /** "08 Sept 2026, 18:41" */
  dateTime: (value: string | null | undefined) => string;
  /** "08 Sept 2026" */
  date: (value: string | null | undefined) => string;
  /** "18:41" — for a narrow column that puts the time under the date. */
  time: (value: string | null | undefined) => string;
  /** "3 minutes ago" */
  relative: (value: string | null | undefined) => string;
}

const RELATIVE_UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 365 * 24 * 60 * 60 * 1000],
  ['month', 30 * 24 * 60 * 60 * 1000],
  ['day', 24 * 60 * 60 * 1000],
  ['hour', 60 * 60 * 1000],
  ['minute', 60 * 1000],
];

/**
 * Dates and relative times follow the active language, so a Russian interface
 * does not print English month names.
 */
export const useFormat = (): Formatters => {
  const { locale, t } = useT();

  return useMemo(() => {
    const tag = INTL_LOCALES[locale];
    const dateTimeFormatter = new Intl.DateTimeFormat(tag, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
    const dateFormatter = new Intl.DateTimeFormat(tag, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
    const timeFormatter = new Intl.DateTimeFormat(tag, { hour: '2-digit', minute: '2-digit' });
    const relativeFormatter = new Intl.RelativeTimeFormat(tag, { numeric: 'auto' });
    const empty = t('common.empty');

    return {
      dateTime: (value) => (value ? dateTimeFormatter.format(new Date(value)) : empty),
      date: (value) => (value ? dateFormatter.format(new Date(value)) : empty),
      time: (value) => (value ? timeFormatter.format(new Date(value)) : ''),
      relative: (value) => {
        if (!value) return empty;
        const delta = new Date(value).getTime() - Date.now();
        for (const [unit, size] of RELATIVE_UNITS) {
          if (Math.abs(delta) >= size) {
            return relativeFormatter.format(Math.round(delta / size), unit);
          }
        }
        return relativeFormatter.format(0, 'second');
      },
    };
  }, [locale, t]);
};
